import { Decoder } from './decoder';
import { Result } from './result';

import * as decoders from './index';

/** Maps the result of a decoder. */
const mapCombinator = <A, B>(decoder: Decoder<A>, f: (value: A) => B) =>
    Decoder.create((value) => decoder.decode(value).map(f));

/** Extends the current decoder, using the result it produces to create another decoder which is then applied to the value in sequence. */
const bindCombinator = <A, B>(decoder: Decoder<A>, f: (value: A) => Decoder<B>) =>
    Decoder.create((input) => decoder.decode(input).flatMap((output) => f(output).decode(input)));

/** Extends the current decoder so that both are tried in sequence, succeeding if either succeeds. */
const alternativeCombinator = <A, B>(firstDecoder: Decoder<A>, secondDecoder: Decoder<B>) =>
    Decoder.create((value) =>
        firstDecoder.decode(value).match(
            (value) => Result.ok<A | B>(value),
            (error) =>
                secondDecoder.decode(value).match(
                    (value) => Result.ok(value),
                    (otherError) => Result.error(error.append(otherError)),
                ),
        ),
    );

/** Extends the current decoder to also succeed if the value is `undefined`. */
const optionalCombinator = <A>(decoder: Decoder<A>) => alternativeCombinator(decoder, decoders.undefined);

/** Extends the current decoder to also succeed if the value is `null`. */
const nullableCombinator = <A>(decoder: Decoder<A>) => alternativeCombinator(decoder, decoders.null);

/** If the decode fails because the value is `undefined`, instead succeed with the provided value. */
const defaultCombinator = <A>(decoder: Decoder<A>, value: A) =>
    alternativeCombinator(
        decoder,
        decoders.undefined.map(() => value),
    );

export {
    mapCombinator as map,
    bindCombinator as bind,
    alternativeCombinator as alternative,
    optionalCombinator as optional,
    nullableCombinator as nullable,
    defaultCombinator as default,
};
